import { useEffect, useState } from "react";
import { MessageCircle, X, ArrowLeft } from "lucide-react";
import { useToggle } from "../contexts/ToggleContext";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import { api } from "../lib/api";

export function ChatbotWidget() {
    const enabled = useToggle("chatbot");
    const { settings } = useSiteSettings();
    const [open, setOpen] = useState(false);
    const [qas, setQas] = useState([]);
    const [active, setActive] = useState(null);

    useEffect(() => {
        if (!open || qas.length) return;
        api.get("/chatbot/qa")
            .then(({ data }) => setQas(data || []))
            .catch(() => setQas([]));
    }, [open, qas.length]);

    if (!enabled) return null;

    const greeting = settings?.chatbotGreeting || "Hi! I'm Sukaanyas Assistant. How can I help?";

    return (
        <>
            {!open ? (
                <button
                    onClick={() => setOpen(true)}
                    className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-skyblueDeep text-white px-4 py-3 shadow-lift hover:scale-[1.04] transition-transform"
                    aria-label="Open chatbot"
                    data-testid="chatbot-launcher-button"
                >
                    <MessageCircle size={20} />
                    <span className="hidden sm:inline text-sm font-medium">Ask the atelier</span>
                </button>
            ) : (
                <div
                    className="fixed bottom-6 right-6 z-50 w-[calc(100%-3rem)] sm:w-[380px] max-w-[380px] bg-ivory rounded-2xl shadow-lift border border-inkDark/5 overflow-hidden flex flex-col animate-fade-up"
                    style={{ maxHeight: "min(75vh, 560px)" }}
                    data-testid="chatbot-panel"
                >
                    <div className="bg-skyblueDeep text-white px-5 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <MessageCircle size={18} />
                            <span className="font-display text-lg leading-none">Sukaanyas Assistant</span>
                        </div>
                        <button
                            onClick={() => { setOpen(false); setActive(null); }}
                            className="text-white/90 hover:text-white"
                            aria-label="Close chatbot"
                            data-testid="chatbot-close-button"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-5 bg-ivory" data-testid="chatbot-body">
                        <div className="bg-beige rounded-2xl rounded-tl-none px-4 py-3 text-sm text-inkDark max-w-[90%]">
                            {greeting}
                        </div>

                        {active ? (
                            <div className="mt-5 space-y-3">
                                <div className="bg-skyblue/40 rounded-2xl rounded-tr-none px-4 py-3 text-sm text-inkDark max-w-[90%] ml-auto" data-testid="chatbot-question-bubble">
                                    {active.question}
                                </div>
                                <div className="bg-beige rounded-2xl rounded-tl-none px-4 py-3 text-sm text-inkDark max-w-[90%] leading-relaxed" data-testid="chatbot-answer-bubble">
                                    {active.answer}
                                </div>
                                <button
                                    onClick={() => setActive(null)}
                                    className="mt-2 inline-flex items-center gap-1 text-sm text-skyblueDeep hover:underline"
                                    data-testid="chatbot-ask-another-button"
                                >
                                    <ArrowLeft size={14} /> Ask another question
                                </button>
                            </div>
                        ) : (
                            <div className="mt-5">
                                <div className="label-eyebrow mb-3">Choose a question</div>
                                <div className="flex flex-col gap-2" data-testid="chatbot-question-list">
                                    {qas.length === 0 ? (
                                        <div className="text-sm text-inkMuted">Loading questions…</div>
                                    ) : qas.map((qa) => (
                                        <button
                                            key={qa.id}
                                            onClick={() => setActive(qa)}
                                            className="text-left text-sm px-4 py-2.5 rounded-xl bg-white border border-inkDark/10 text-inkDark hover:border-skyblueDeep hover:bg-skyblue/15 transition-colors"
                                            data-testid={`chatbot-question-${qa.order}`}
                                        >
                                            {qa.question}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="px-5 py-3 border-t border-inkDark/5 bg-ivory text-[10px] tracking-[0.2em] uppercase text-inkMuted text-center">
                        Powered by Sukaanyas Atelier
                    </div>
                </div>
            )}
        </>
    );
}
